import type { ApiRoutes } from '~/api/pipelineV2';

export function collectMethodIdsFromRoutes(
  routes: ApiRoutes[] | undefined
): string[] {
  if (!Array.isArray(routes)) {
    return [];
  }
  return routes.flatMap(r => (r.methods ?? []).map(m => m.id));
}

export type FlatEndpointRow = {
  routeIndex: number;
  methodId: string;
  path: string;
  method: string;
  summary: string;
};

export function flattenRoutesToRows(
  routes: ApiRoutes[] | undefined
): FlatEndpointRow[] {
  if (!Array.isArray(routes)) {
    return [];
  }
  const rows: FlatEndpointRow[] = [];
  routes.forEach((route, routeIndex) => {
    (route.methods ?? []).forEach(m => {
      rows.push({
        routeIndex,
        methodId: m.id,
        path: route.path,
        method: m.method,
        summary: m.summary ?? ''
      });
    });
  });
  return rows;
}

export function updateRoutePathForMethod(
  routes: ApiRoutes[],
  methodId: string,
  path: string
): ApiRoutes[] {
  return routes.map(r =>
    r.methods.some(m => m.id === methodId) ? { ...r, path } : r
  );
}

export function updateMethodFieldsForMethodId(
  routes: ApiRoutes[],
  methodId: string,
  patch: { method?: string; summary?: string }
): ApiRoutes[] {
  return routes.map(r => ({
    ...r,
    methods: r.methods.map(m => (m.id === methodId ? { ...m, ...patch } : m))
  }));
}

/** Removes the method with the given id; drops empty path entries. */
export function removeMethodById(
  routes: ApiRoutes[],
  methodId: string
): ApiRoutes[] {
  return routes
    .map(r => ({
      ...r,
      methods: r.methods.filter(m => m.id !== methodId)
    }))
    .filter(r => r.methods.length > 0);
}

/** Keeps only methods whose ids are in activeMethodIds when an API router is present. */
export function pruneRestApiTriggerRoutes(
  routes: ApiRoutes[] | undefined,
  activeMethodIds: Set<string>,
  options: { hasApiRouter: boolean }
): ApiRoutes[] {
  if (!options.hasApiRouter) {
    return routes ?? [];
  }
  if (!Array.isArray(routes)) {
    return [];
  }

  return routes
    .map(route => ({
      ...route,
      methods: route.methods.filter(method => activeMethodIds.has(method.id))
    }))
    .filter(route => route.methods.length > 0);
}
